import { createTheme, Theme, ThemeOptions, alpha } from '@mui/material/styles';

export type ThemeMode = 'light' | 'dark';

/**
 * World-class theme system inspired by Ethiopian/Eritrean culture
 *
 * Color Philosophy:
 * - Primary: Rich blue-purple (trust, professionalism, royalty)
 * - Secondary: Vibrant green (Ethiopian flag, growth, prosperity)
 * - Accent: Warm amber/gold (Ethiopian coffee, heritage, warmth)
 * - Error: Deep red (Ethiopian flag, urgency)
 *
 * Design Principles:
 * - Cultural authenticity with modern elegance
 * - WCAG AAA accessibility standards
 * - Smooth transitions and micro-interactions
 * - Consistent spacing using 4px base unit
 * - Professional typography hierarchy
 */

// Color palette inspired by Ethiopian/Eritrean culture
export const culturalColors = {
  // Ethiopian flag colors modernized
  ethiopianGreen: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  ethiopianGold: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  ethiopianRed: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  // Modern accent colors
  royalPurple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7e22ce',
    800: '#6b21a8',
    900: '#581c87',
  },
  deepBlue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
};

/**
 * Creates the application theme with cultural and modern design elements
 */
export const createAppTheme = (mode: ThemeMode): Theme => {
  const isDark = mode === 'dark';

  // Base colors
  const primary = isDark ? culturalColors.royalPurple[400] : culturalColors.deepBlue[600];
  const secondary = isDark ? culturalColors.ethiopianGreen[400] : culturalColors.ethiopianGreen[600];
  const accent = isDark ? culturalColors.ethiopianGold[400] : culturalColors.ethiopianGold[600];
  const error = isDark ? culturalColors.ethiopianRed[400] : culturalColors.ethiopianRed[600];

  // Background colors with depth
  const background = {
    default: isDark ? '#0a0e1a' : '#fafafa',
    paper: isDark ? '#141824' : '#ffffff',
    elevated: isDark ? '#1a1f2e' : '#f5f5f5',
  };

  // Text colors with proper contrast
  const text = {
    primary: isDark ? '#f8fafc' : '#0f172a',
    secondary: isDark ? '#cbd5e1' : '#475569',
    disabled: isDark ? '#64748b' : '#94a3b8',
  };

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: primary,
        light: isDark ? culturalColors.royalPurple[300] : culturalColors.deepBlue[500],
        dark: isDark ? culturalColors.royalPurple[500] : culturalColors.deepBlue[700],
        contrastText: '#ffffff',
      },
      secondary: {
        main: secondary,
        light: isDark ? culturalColors.ethiopianGreen[300] : culturalColors.ethiopianGreen[500],
        dark: isDark ? culturalColors.ethiopianGreen[500] : culturalColors.ethiopianGreen[700],
        contrastText: '#ffffff',
      },
      error: {
        main: error,
        light: isDark ? culturalColors.ethiopianRed[300] : culturalColors.ethiopianRed[500],
        dark: isDark ? culturalColors.ethiopianRed[500] : culturalColors.ethiopianRed[700],
        contrastText: '#ffffff',
      },
      warning: {
        main: accent,
        light: isDark ? culturalColors.ethiopianGold[300] : culturalColors.ethiopianGold[500],
        dark: isDark ? culturalColors.ethiopianGold[500] : culturalColors.ethiopianGold[700],
        contrastText: isDark ? '#0f172a' : '#ffffff',
      },
      info: {
        main: isDark ? '#60a5fa' : '#0284c7',
        light: isDark ? '#93c5fd' : '#0ea5e9',
        dark: isDark ? '#3b82f6' : '#0369a1',
        contrastText: '#ffffff',
      },
      success: {
        main: secondary,
        light: isDark ? culturalColors.ethiopianGreen[300] : culturalColors.ethiopianGreen[500],
        dark: isDark ? culturalColors.ethiopianGreen[500] : culturalColors.ethiopianGreen[700],
        contrastText: '#ffffff',
      },
      background,
      text,
      divider: isDark ? alpha('#ffffff', 0.08) : alpha('#000000', 0.08),
      action: {
        active: isDark ? alpha('#ffffff', 0.7) : alpha('#000000', 0.6),
        hover: isDark ? alpha('#ffffff', 0.08) : alpha('#000000', 0.04),
        selected: isDark ? alpha(primary, 0.16) : alpha(primary, 0.08),
        disabled: isDark ? alpha('#ffffff', 0.3) : alpha('#000000', 0.26),
        disabledBackground: isDark ? alpha('#ffffff', 0.12) : alpha('#000000', 0.12),
        focus: isDark ? alpha(primary, 0.24) : alpha(primary, 0.12),
      },
    },
    typography: {
      fontFamily: 'var(--font-inter), Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      // Modern scale using 1.25 ratio (Major Third)
      h1: {
        fontFamily: 'var(--font-poppins), Poppins, var(--font-inter), sans-serif',
        fontWeight: 800,
        fontSize: '3.052rem', // 48.83px
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontFamily: 'var(--font-poppins), Poppins, var(--font-inter), sans-serif',
        fontWeight: 700,
        fontSize: '2.441rem', // 39.06px
        lineHeight: 1.2,
        letterSpacing: '-0.015em',
      },
      h3: {
        fontFamily: 'var(--font-poppins), Poppins, var(--font-inter), sans-serif',
        fontWeight: 700,
        fontSize: '1.953rem', // 31.25px
        lineHeight: 1.25,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontFamily: 'var(--font-poppins), Poppins, var(--font-inter), sans-serif',
        fontWeight: 600,
        fontSize: '1.563rem', // 25px
        lineHeight: 1.3,
        letterSpacing: '-0.005em',
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem', // 20px
        lineHeight: 1.4,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem', // 16px
        lineHeight: 1.5,
      },
      subtitle1: {
        fontSize: '1.125rem', // 18px
        lineHeight: 1.6,
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: '1rem', // 16px
        lineHeight: 1.6,
        fontWeight: 500,
      },
      body1: {
        fontSize: '1rem', // 16px
        lineHeight: 1.7,
        fontWeight: 400,
      },
      body2: {
        fontSize: '0.875rem', // 14px
        lineHeight: 1.6,
        fontWeight: 400,
      },
      button: {
        fontSize: '0.9375rem', // 15px
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: '0.01em',
      },
      caption: {
        fontSize: '0.75rem', // 12px
        lineHeight: 1.5,
        fontWeight: 400,
      },
      overline: {
        fontSize: '0.75rem', // 12px
        lineHeight: 1.5,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    spacing: 4, // 4px base unit for consistent spacing
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1280,
      },
    },
    shadows: [
      'none',
      // Elevated shadows for depth
      isDark
        ? '0px 2px 4px rgba(0, 0, 0, 0.4)'
        : '0px 2px 4px rgba(15, 23, 42, 0.04)',
      isDark
        ? '0px 4px 8px rgba(0, 0, 0, 0.4)'
        : '0px 4px 8px rgba(15, 23, 42, 0.06)',
      isDark
        ? '0px 8px 16px rgba(0, 0, 0, 0.5)'
        : '0px 8px 16px rgba(15, 23, 42, 0.08)',
      isDark
        ? '0px 12px 24px rgba(0, 0, 0, 0.5)'
        : '0px 12px 24px rgba(15, 23, 42, 0.1)',
      isDark
        ? '0px 16px 32px rgba(0, 0, 0, 0.6)'
        : '0px 16px 32px rgba(15, 23, 42, 0.12)',
      isDark
        ? '0px 20px 40px rgba(0, 0, 0, 0.6)'
        : '0px 20px 40px rgba(15, 23, 42, 0.14)',
      isDark
        ? '0px 24px 48px rgba(0, 0, 0, 0.7)'
        : '0px 24px 48px rgba(15, 23, 42, 0.16)',
      isDark
        ? '0px 32px 64px rgba(0, 0, 0, 0.7)'
        : '0px 32px 64px rgba(15, 23, 42, 0.18)',
      // Additional shadow levels (9-24)
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
      isDark ? '0px 32px 64px rgba(0, 0, 0, 0.8)' : '0px 32px 64px rgba(15, 23, 42, 0.2)',
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: isDark
              ? `${alpha(primary, 0.3)} transparent`
              : `${alpha(primary, 0.2)} transparent`,
            // Smooth scrolling
            scrollBehavior: 'smooth',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '10px 24px',
            fontSize: '0.9375rem',
            fontWeight: 600,
            boxShadow: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: isDark
                ? '0 8px 16px rgba(0, 0, 0, 0.4)'
                : '0 8px 16px rgba(15, 23, 42, 0.1)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: isDark
                ? '0 12px 24px rgba(0, 0, 0, 0.5)'
                : '0 12px 24px rgba(15, 23, 42, 0.15)',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
            },
          },
          sizeLarge: {
            padding: '14px 32px',
            fontSize: '1rem',
            borderRadius: 12,
          },
          sizeSmall: {
            padding: '6px 16px',
            fontSize: '0.875rem',
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : alpha('#000000', 0.06)}`,
            backgroundImage: 'none',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: isDark
                ? '0 20px 40px rgba(0, 0, 0, 0.6)'
                : '0 20px 40px rgba(15, 23, 42, 0.12)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: 12,
          },
          elevation1: {
            boxShadow: isDark
              ? '0 2px 8px rgba(0, 0, 0, 0.4)'
              : '0 2px 8px rgba(15, 23, 42, 0.06)',
          },
          elevation2: {
            boxShadow: isDark
              ? '0 4px 12px rgba(0, 0, 0, 0.4)'
              : '0 4px 12px rgba(15, 23, 42, 0.08)',
          },
          elevation3: {
            boxShadow: isDark
              ? '0 8px 16px rgba(0, 0, 0, 0.5)'
              : '0 8px 16px rgba(15, 23, 42, 0.1)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDark ? alpha(primary, 0.5) : alpha(primary, 0.4),
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                },
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? alpha('#ffffff', 0.95) : alpha('#0f172a', 0.95),
            color: isDark ? '#0f172a' : '#ffffff',
            fontSize: '0.75rem',
            fontWeight: 500,
            borderRadius: 8,
            padding: '8px 12px',
            boxShadow: isDark
              ? '0 8px 16px rgba(0, 0, 0, 0.5)'
              : '0 8px 16px rgba(15, 23, 42, 0.15)',
          },
          arrow: {
            color: isDark ? alpha('#ffffff', 0.95) : alpha('#0f172a', 0.95),
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
          },
        },
      },
    },
  });

  return theme;
};

// Export pre-configured themes
export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');
